// Shared "fetch every page of a paginated list endpoint" helper.
//
// The pattern this replaces — fetch page 1, then Promise.all() every
// remaining page at once — is fine for a small, bounded reference table,
// but turns into a burst of dozens or hundreds of simultaneous requests
// against any endpoint whose row count keeps growing (an audit log that
// logs every action in the system, for instance). This fetches remaining
// pages in small concurrent batches instead of all at once, so "give me
// everything" never turns into a request storm — same completeness, paced
// differently.
const DEFAULT_BATCH_SIZE = 4;

// Prefers an explicit page count; falls back to deriving one from a total
// row count + page size (covers DRF-style `{ results, count }` backends
// that don't send `last_page` directly).
function resolveLastPage(pagination, pageSize) {
  const explicit = Number(
    pagination?.last_page ??
    pagination?.total_pages ??
    pagination?.page_count
  );
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  const total = Number(pagination?.total ?? pagination?.count ?? pagination?.total_count);
  const size = Number(pageSize ?? pagination?.per_page ?? pagination?.page_size);
  if (Number.isFinite(total) && Number.isFinite(size) && size > 0) {
    return Math.max(1, Math.ceil(total / size));
  }

  return 1;
}

// `fetchPage(pageNumber)` must resolve to `{ items, pagination }`.
// `firstPage` lets a caller pass in a page-1 response it already fetched
// (e.g. to check `pagination.total` before deciding to fetch the rest).
// `pageSize` only matters for the derive-from-total fallback above.
export async function fetchAllPages(fetchPage, { firstPage, batchSize = DEFAULT_BATCH_SIZE, pageSize } = {}) {
  const first = firstPage ?? await fetchPage(1);
  const resolvedPageSize = pageSize ?? (first.items.length || undefined);
  const lastPage = resolveLastPage(first.pagination, resolvedPageSize);

  if (lastPage <= 1) {
    return first;
  }

  const remainingPageNumbers = Array.from({ length: lastPage - 1 }, (_, index) => index + 2);
  const restPages = [];

  for (let start = 0; start < remainingPageNumbers.length; start += batchSize) {
    const batch = remainingPageNumbers.slice(start, start + batchSize);
    // eslint-disable-next-line no-await-in-loop -- deliberately sequential across batches, concurrent within one
    const batchResults = await Promise.all(batch.map(fetchPage));
    restPages.push(...batchResults);
  }

  return {
    items: [first.items, ...restPages.map((page) => page.items)].flat(),
    pagination: first.pagination,
  };
}
